import React, { useEffect, useRef, useState } from 'react';
import { api, apiErrorMessage } from '../api/client';
import { DH680Part, DH680Summary } from '../api/dh680';

interface GridRow {
  row: string;
  label: string;
  doe_code: string;
  dates: string[];
}

interface Undated {
  record_id: string;
  vaccine_name: string;
  dose_number: number | null;
}

interface Prefill {
  signer_name: string;
  license_number: string;
  clinic_name: string;
  clinic_address: string;
  clinic_phone: string;
}

export interface PrepareResponse {
  student: { first_name: string; last_name: string; date_of_birth: string; grade_level: number };
  grid: GridRow[];
  undated: Undated[];
  varicella_disease_year?: number | null;
  compliance_status: string;
  v2_status?: string | null;
  current: DH680Summary | null;
  prefill: Prefill;
  suggested_part: DH680Part;
}

interface Props {
  studentId: string;
  onClose: () => void;
  onIssued: (result: { certificate_id: string; serial: string }) => void;
}

const fmt = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${m}/${d}/${y.slice(2)}` : iso;
};

const FIELDS: { key: keyof Prefill; label: string; group: 'Signature' | 'Clinic'; rows?: number }[] = [
  { key: 'signer_name', label: 'Full name', group: 'Signature' },
  { key: 'license_number', label: 'License number', group: 'Signature' },
  { key: 'clinic_name', label: 'Name', group: 'Clinic' },
  { key: 'clinic_address', label: 'Address', group: 'Clinic', rows: 2 },
  { key: 'clinic_phone', label: 'Phone', group: 'Clinic' },
];

const MIN_LEN: Record<keyof Prefill, number> = {
  signer_name: 2,
  license_number: 1,
  clinic_name: 2,
  clinic_address: 5,
  clinic_phone: 7,
};

/**
 * "Issue DH 680" pop-up (docs/DH680_PDF_ISSUANCE_PLAN.md §8.2). One panel, no
 * steps. The nurse picks the Part; compliance status is shown as a hint only.
 */
const IssueDH680Dialog: React.FC<Props> = ({ studentId, onClose, onIssued }) => {
  const [data, setData] = useState<PrepareResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [part, setPart] = useState<DH680Part | null>(null);
  const [expiration, setExpiration] = useState('');
  const [fields, setFields] = useState<Prefill>({
    signer_name: '',
    license_number: '',
    clinic_name: '',
    clinic_address: '',
    clinic_phone: '',
  });
  const [confirm, setConfirm] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/api/admin/students/${studentId}/dh680/prepare`)
      .then((res) => {
        if (!alive) return;
        const body = res.data as PrepareResponse;
        setData(body);
        setFields(body.prefill);
        setPart(body.suggested_part);
      })
      .catch((err) => alive && setLoadError(apiErrorMessage(err, 'Could not load the record.')));
    return () => {
      alive = false;
    };
  }, [studentId]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const fieldsOk = (Object.keys(MIN_LEN) as (keyof Prefill)[]).every(
    (k) => fields[k].trim().length >= MIN_LEN[k],
  );
  const canSubmit =
    !!data &&
    data.undated.length === 0 &&
    !!part &&
    (part !== 'B' || !!expiration) &&
    fieldsOk &&
    confirm &&
    !submitting;

  const submit = async () => {
    if (!canSubmit || !part) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: Record<string, any> = {
        part,
        part_b_expiration: part === 'B' ? expiration : null,
        ...fields,
        confirm: true,
      };
      if (overrideReason.trim()) {
        payload.override_reason = overrideReason.trim();
      }
      const res = await api.post(`/api/admin/students/${studentId}/dh680`, payload);
      onIssued(res.data as { certificate_id: string; serial: string });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not issue the DH 680.'));
    } finally {
      setSubmitting(false);
    }
  };

  const title = data
    ? `Issue DH 680 — ${data.student.first_name} ${data.student.last_name} (DOB ${data.student.date_of_birth})`
    : 'Issue DH 680';
  const printedRows = data ? data.grid.filter((r) => r.dates.length > 0) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl focus:outline-none"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded px-2 text-lg leading-none text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <div className="space-y-5 px-5 py-4 text-sm">
          {loadError && <div className="rounded bg-red-50 px-3 py-2 text-red-700">{loadError}</div>}
          {!data && !loadError && <div className="text-slate-500">Loading…</div>}
          {data && (
            <>
              <section>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Doses (as printed on the form)
                </h3>
                {printedRows.length === 0 ? (
                  <p className="text-slate-500">No school-required doses on file.</p>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {printedRows.map((r) => (
                        <tr key={r.row} className="border-b border-slate-100">
                          <td className="py-1 pr-2 font-medium text-slate-800">{r.label}</td>
                          {r.dates.map((d) => (
                            <td key={d} className="py-1 pr-2 font-mono text-slate-700">
                              {fmt(d)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {data.undated.length > 0 && (
                  <p role="alert" className="mt-2 rounded bg-amber-50 px-3 py-2 text-amber-800">
                    ⚠ {data.undated.length} dose{data.undated.length === 1 ? ' has' : 's have'} no date (
                    {data.undated
                      .map((u) => `${u.vaccine_name}${u.dose_number ? ` dose ${u.dose_number}` : ''}`)
                      .join(', ')}
                    ). Fix it before issuing.
                  </p>
                )}
                <p className="mt-2 text-xs text-slate-500">
                  Current status: {data.compliance_status} (hint only)
                </p>
                <p className="mt-1 rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
                  Verify every record against the Florida Immunization Guidelines (DH 150-615) and
                  original card / Florida SHOTS before certifying.
                </p>
                {part && part !== 'B' && data.compliance_status !== 'COMPLIANT' && (
                  <label className="mt-2 block text-xs text-slate-700">
                    Clinical override reason (required if issuing Part A while status is not COMPLIANT, min 15 chars):
                    <input
                      type="text"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      placeholder="e.g. Verified external SHOTS history on file"
                      className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
                    />
                  </label>
                )}
                {data.current && (
                  <p className="mt-1 text-xs text-slate-500">
                    Issuing replaces {data.current.serial}, which will be marked voided.
                  </p>
                )}
              </section>

              <fieldset>
                <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Certificate
                </legend>
                {(
                  [
                    ['A1', 'Part A – Code 1 (K-6)'],
                    ['A8', 'Part A – Code 8 (7th grade+)'],
                    ['B', 'Part B – Temporary medical exemption'],
                  ] as [DH680Part, string][]
                ).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 py-0.5">
                    <input
                      type="radio"
                      name="dh680-part"
                      value={value}
                      checked={part === value}
                      onChange={() => setPart(value)}
                    />
                    {label}
                  </label>
                ))}
                {part === 'B' && (
                  <label className="ml-6 mt-1 flex items-center gap-2">
                    Expires
                    <input
                      type="date"
                      aria-label="Expiration date"
                      value={expiration}
                      onChange={(e) => setExpiration(e.target.value)}
                      className="rounded border border-slate-300 px-2 py-1"
                    />
                  </label>
                )}
              </fieldset>

              {(['Signature', 'Clinic'] as const).map((group) => (
                <fieldset key={group} className="grid grid-cols-[8rem_1fr] items-start gap-2">
                  <legend className="col-span-2 mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {group}
                  </legend>
                  {FIELDS.filter((f) => f.group === group).map((f) => {
                    const id = `dh680-${f.key}`;
                    const common = {
                      id,
                      value: fields[f.key],
                      onChange: (
                        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
                      ) => setFields((prev) => ({ ...prev, [f.key]: e.target.value })),
                      className: 'w-full rounded border border-slate-300 px-2 py-1',
                    };
                    return (
                      <React.Fragment key={f.key}>
                        <label htmlFor={id} className="pt-1 text-slate-600">
                          {f.label}
                        </label>
                        {f.rows ? <textarea rows={f.rows} {...common} /> : <input type="text" {...common} />}
                      </React.Fragment>
                    );
                  })}
                </fieldset>
              ))}

              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={confirm}
                  onChange={(e) => setConfirm(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I reviewed the guardian's documents and I am signing this DH 680 electronically.
                </span>
              </label>

              {error && (
                <div role="alert" className="rounded bg-red-50 px-3 py-2 text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
          >
            {submitting ? 'Signing…' : 'Sign & issue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssueDH680Dialog;
